import { Link } from "react-router-dom";
import {
  Home as HomeIcon,
  Utensils,
  ShowerHead,
  Brain,
  HandHeart,
  GraduationCap,
  Briefcase,
  Moon,
} from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const RIPPLE = "https://static.prod-images.emergentagent.com/jobs/7c3bd71d-f3a1-4475-868e-2031ff56ad92/images/0da293f3e2da169b65ef1706c0ba1a148d5f9fb022de9609c007727662d20643.png";

const PILLARS = [
  { icon: HomeIcon, label: "Crisis accommodation", text: "Safe, confidential and immediate short-term shelter." },
  { icon: Utensils, label: "Food & nutrition", text: "Drop-in meals, hampers, and mobile food vans." },
  { icon: ShowerHead, label: "Laundry & hygiene", text: "Showers, laundry, hygiene products and material aid." },
  { icon: Brain, label: "Mental health support", text: "Trauma-informed casework and dual-diagnosis counselling." },
  { icon: HandHeart, label: "Mentorship & casework", text: "Individualised support that follows the person." },
  { icon: GraduationCap, label: "Life skills development", text: "Workshops in communication, leadership and conflict resolution." },
  { icon: Briefcase, label: "Education & employment", text: "Pathways into school, training, traineeships and work." },
  { icon: Moon, label: "After-hours engagement", text: "Supervised evening activities for young people." },
];

const COMMITMENTS = [
  { n: "01", label: "Trauma-informed care" },
  { n: "02", label: "Dignity services" },
  { n: "03", label: "Holistic, non-judgmental support" },
  { n: "04", label: "Safe spaces, always" },
  { n: "05", label: "Cultural respect & inclusion" },
  { n: "06", label: "Long-term stability over short-term fixes" },
];

export default function Impact() {
  useReveal();
  return (
    <div data-testid="impact-page">
      {/* HERO */}
      <section className="ripple-bg">
        <div className="container-app pt-24 sm:pt-32 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow">Community impact</p>
              <h1 className="font-heading mt-6 text-[44px] sm:text-[64px] lg:text-[76px] leading-[0.98] tracking-[-0.02em] font-semibold text-[#0A2540]">
                Building meaningful community support.
              </h1>
            </div>
            <div className="lg:col-span-4 reveal">
              <p className="text-lg text-slate-600 leading-relaxed">
                CLEAN Water Drop Association is a growing community initiative focused on
                practical support, dignity, inclusion, and hope — through trauma-informed
                care, dignity services, and long-term stability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RIPPLE FEATURE IMAGE */}
      <section className="container-app pb-24">
        <div className="relative aspect-[21/9] rounded-[2.5rem] overflow-hidden border border-slate-100 reveal">
          <img src={RIPPLE} alt="A single drop of water creating ripples" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/40 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 sm:bottom-12 sm:left-12 sm:right-12">
            <p className="font-heading text-white text-xl sm:text-2xl lg:text-3xl leading-snug max-w-2xl drop-shadow-md">
              One conversation, one meal, one shower — the ripple often begins quietly.
            </p>
          </div>
        </div>
      </section>

      {/* WHO WE WALK ALONGSIDE */}
      <section className="section-y border-t border-slate-100">
        <div className="container-app">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-4 reveal">
              <p className="eyebrow">Who we walk alongside</p>
              <h2 className="font-heading mt-5 text-4xl sm:text-5xl text-[#0A2540] leading-[1.05] tracking-tight">
                Women, young people, and those experiencing homelessness.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 reveal">
              <p className="text-xl text-slate-700 leading-[1.6] font-light">
                Many of the people we support face overlapping challenges — homelessness,
                trauma, mental health and disconnection from positive networks.
              </p>
              <p className="mt-6 text-base text-slate-600 leading-relaxed">
                We meet them where they are, without judgment, and stay with them through
                the journey toward long-term stability. As a growing community initiative,
                we measure our work in care, consistency, and trust — not headline numbers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE PILLARS */}
      <section className="section-y bg-[#F8FAFC]">
        <div className="container-app">
          <div className="max-w-2xl reveal mb-14">
            <p className="eyebrow">Service pillars</p>
            <h2 className="font-heading mt-5 text-4xl sm:text-5xl text-[#0A2540] leading-[1.05] tracking-tight">
              The everyday work, in eight focus areas.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {PILLARS.map((p, i) => (
              <div
                key={p.label}
                className="rounded-3xl bg-white border border-slate-100 p-8 reveal"
                style={{ transitionDelay: `${i * 40}ms` }}
                data-testid={`pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#E6F0F2] text-[#2E8B8B]">
                  <p.icon className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <p className="font-heading mt-6 text-lg text-[#0A2540] leading-snug">{p.label}</p>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY COMMITMENTS — replaces invented statistics */}
      <section className="section-y">
        <div className="container-app">
          <div className="max-w-2xl reveal mb-14">
            <p className="eyebrow">Community commitments</p>
            <h2 className="font-heading mt-5 text-4xl sm:text-5xl text-[#0A2540] leading-[1.05] tracking-tight">
              How we show up — every day.
            </h2>
          </div>
          <div className="grid divide-y divide-slate-200 border-y border-slate-200">
            {COMMITMENTS.map((c) => (
              <div
                key={c.n}
                className="grid grid-cols-12 py-7 sm:py-8 items-baseline reveal"
                data-testid={`commitment-${c.label.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`}
              >
                <p className="col-span-2 text-xs uppercase tracking-[0.22em] text-slate-400">{c.n}</p>
                <p className="col-span-10 text-xl sm:text-2xl text-[#0A2540] font-heading leading-snug">
                  {c.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-app py-24">
        <div className="rounded-[2.5rem] bg-[#0A2540] text-white p-12 sm:p-16 grid lg:grid-cols-2 gap-10 items-end">
          <div>
            <p className="text-xs uppercase tracking-[0.26em] text-[#3AABAB]">Help us build this work</p>
            <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              Support a service. Strengthen the community.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 lg:justify-end">
            <Link to="/donate" data-testid="impact-donate-cta" className="btn-secondary">Donate</Link>
            <Link to="/volunteer" data-testid="impact-volunteer-cta" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 px-7 py-3.5 text-sm font-medium text-white hover:bg-white hover:text-[#0A2540] transition-all">
              Volunteer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
